'use client'

import { useAuth } from '@clerk/nextjs'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'

import { useAppLocale } from '@/components/i18n/locale-provider'
import {
  type UpgradeLimitConfig,
  type UpgradeLimitCta,
  withPromoLimitSecondaryCta,
} from '@/components/upgrade/upgrade-limit-config'

// Split the dialog (WebGL dither background via @paper-design/shaders-react
// + full-feature motion) out of the always-loaded authed-providers chunk.
// This provider mounts on EVERY authed page but the dialog is only shown
// after a usage limit fires; `config === null` until then, so the chunk
// loads on the same tick as the first blocked action instead of everywhere.
const UpgradeLimitDialog = dynamic(
  () =>
    import('@/components/upgrade/upgrade-limit-dialog').then(
      (mod) => mod.UpgradeLimitDialog
    ),
  { ssr: false }
)

// biome-ignore lint/suspicious/noImportCycles: pre-existing import cycle (madge-era baseline) — do not extend; unwind when touching this module
import { useUsageLimitsOptional } from '@/contexts/usage-limits-context'
import { useOptionalCan } from '@/hooks/use-can'
import { changePlanAction } from '@/lib/actions/billing'
import { CAPABILITY_DENIED_COPY } from '@/lib/auth/capability-copy'
import { getBillingPlanChangeDirection } from '@/lib/billing/plan-transitions'
import {
  BILLING_PLANS,
  isBillingPlanKey,
  type SelfServePlanKey,
} from '@/lib/billing/plans'
import {
  defaultPriceSummary,
  formatCurrency,
} from '@/lib/billing/price-summary'
import { isPromoEligible } from '@/lib/billing/promo'
import {
  buildPrepaidTermChangeBlockedMessage,
  isInAppRepriceableStatus,
  isInPrepaidTerm,
  shouldDeferPlanChangeToPlanPage,
} from '@/lib/billing/ui-state'
import { localizeErrorMessage } from '@/lib/errors/error-message-localizer'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { appToast } from '@/lib/shared/toast'
import {
  startTrackedCheckout,
  trackInAppPlanUpgrade,
  trackInAppPlanUpgradeStarted,
} from '@/lib/site/checkout-tracking'
import { trackUsageLimitHit } from '@/lib/site/gtm'
import {
  buildCreditLimitHitDedupKey,
  buildCreditLimitHitEvent,
} from '@/lib/site/usage-limit-tracking'

type UpgradeLimitContextValue = {
  /** Open the upgrade dialog with a config (e.g. from buildBrandLimitConfig). */
  showUpgrade: (config: UpgradeLimitConfig) => void
  /** Close the dialog programmatically. */
  hideUpgrade: () => void
  /** True when the primary upgrade CTA is starting Stripe checkout. */
  isUpgradePending: boolean
}

const UpgradeLimitContext = createContext<UpgradeLimitContextValue | null>(null)

export function UpgradeLimitDialogProvider({
  children,
}: {
  children: ReactNode
}) {
  const canManageBilling = useOptionalCan('org.billing')
  const router = useRouter()
  const t = useTranslations('appShell')
  const locale = useAppLocale()
  const usage = useUsageLimitsOptional()
  // `useAuth` is safe here because this provider is only ever mounted inside
  // ClerkProvider (see `AuthedAppShell`). The values are used purely for GTM
  // attribution and Stripe checkout — never to gate UI.
  const { userId, orgId } = useAuth()
  const [config, setConfig] = useState<UpgradeLimitConfig | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  // Hydration-stable clock for render-time prepaid-term checks (repo pattern:
  // app/(app)/plan/plan-content.tsx). Click handlers use a fresh Date.now().
  const nowMsRef = useRef(Date.now())
  const [isUpgradePending, startUpgradeTransition] = useTransition()

  const showUpgrade = useCallback(
    (nextConfig: UpgradeLimitConfig) => {
      const promoEligible =
        usage !== null &&
        !usage.isLoading &&
        isBillingPlanKey(usage.planKey) &&
        isPromoEligible({
          planKey: usage.planKey,
          hasEverPaid: usage.hasEverPaid,
          targetPlanKey: nextConfig.recommendedPlanKey,
        })
      setConfig(withPromoLimitSecondaryCta(nextConfig, promoEligible, locale))
      setIsOpen(true)

      const limitHit = buildCreditLimitHitEvent({
        limitType: nextConfig.limitType,
        remainingCredits: usage?.remainingCredits,
        userId,
        orgId,
        planName: usage?.planName,
        currentPeriodEnd: usage?.currentPeriodEnd,
      })

      // The named lifecycle event means true credit exhaustion, not every
      // reason an upgrade dialog can open. Dedup per org and billing period.
      if (typeof window !== 'undefined') {
        const dedupKey = buildCreditLimitHitDedupKey({
          orgId,
          currentPeriodEnd: usage?.currentPeriodEnd,
        })
        let hasTracked = false
        try {
          hasTracked = window.sessionStorage.getItem(dedupKey) === '1'
        } catch {
          // Storage is an optimization, not a prerequisite. The stable
          // transaction_id lets Google deduplicate a later browser retry.
        }
        if (limitHit && !hasTracked) {
          trackUsageLimitHit(limitHit)
          try {
            window.sessionStorage.setItem(dedupKey, '1')
          } catch {
            // Best-effort local dedup; the event has already been emitted.
          }
        }
      }
    },
    [usage, userId, orgId, locale]
  )

  const hideUpgrade = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen)
  }, [])

  const handlePrimary = useCallback(
    (cta: UpgradeLimitCta) => {
      if (cta.href) {
        window.open(cta.href, '_blank', 'noopener,noreferrer')
        setIsOpen(false)
        return
      }

      // Billing is org-admin work: a member sees why, never a checkout.
      if (cta.planKey && canManageBilling === false) {
        appToast.warning(CAPABILITY_DENIED_COPY['org.billing'])
        setIsOpen(false)
        return
      }

      if (cta.planKey) {
        const planKey: SelfServePlanKey = cta.planKey
        const planName = BILLING_PLANS[planKey].name
        // An org that already has paid billing cannot start a second checkout
        // (`createOrgCheckoutSession` 409s). Reprice the existing subscription
        // in place instead; the reactive usage snapshot refreshes credits.
        const isExistingPaidOrg = usage?.portalEligible ?? false
        // Scheduled changes, payment issues, and prepaid terms all reject an
        // in-app reprice. /plan renders the correct recovery affordance.
        const shouldDeferToPlanPage = shouldDeferPlanChangeToPlanPage({
          portalEligible: isExistingPaidOrg,
          hasScheduledPlanChange: usage?.hasScheduledPlanChange ?? false,
          status: usage?.status,
          isGrowthCommitment: usage?.isGrowthCommitment,
          currentPeriodEnd: usage?.currentPeriodEnd,
          // Click-time clock: the dialog can sit open across the term boundary.
          nowMs: Date.now(),
        })
        // Until the usage snapshot has loaded we can't tell free from paid, and
        // blindly starting checkout for a paid org 409s. In that window, hand
        // off to /plan, which renders the correct action for any billing state.
        const isUsageReady = !!usage && !usage.isLoading

        startUpgradeTransition(async () => {
          if (shouldDeferToPlanPage) {
            router.push('/plan')
            setIsOpen(false)
            return
          }

          if (isExistingPaidOrg) {
            try {
              const price = defaultPriceSummary()[planKey]
              if (
                usage &&
                isBillingPlanKey(usage.planKey) &&
                getBillingPlanChangeDirection({
                  currentPlanKey: usage.planKey,
                  nextPlanKey: planKey,
                }) === 'upgrade'
              ) {
                void trackInAppPlanUpgradeStarted({
                  userId,
                  orgId,
                  priorPlanName: usage.planName,
                  planKey,
                  planName,
                  price,
                })
              }
              const result = await changePlanAction(planKey)
              switch (result.direction) {
                case 'none':
                  appToast.warning(t('upgrade.alreadyOn', { plan: planName }))
                  break
                case 'kept':
                  appToast.success(t('upgrade.stayingOn', { plan: planName }))
                  break
                case 'downgrade':
                  appToast.success(
                    t('upgrade.startsNextCycle', { plan: planName })
                  )
                  break
                case 'upgrade':
                  appToast.success(t('upgrade.upgradedTo', { plan: planName }))
                  trackInAppPlanUpgrade({
                    direction: result.direction,
                    userId,
                    orgId,
                    priorPlanName: usage?.planName,
                    planKey,
                    planName,
                    price,
                    conversion: result.upgradeConversion,
                  })
                  break
              }
              setIsOpen(false)
            } catch (error) {
              appToast.error(getErrorMessage(error, t('upgrade.changeFailed')))
            }
            return
          }

          if (!isUsageReady) {
            router.push('/plan')
            setIsOpen(false)
            return
          }

          // Free / unsubscribed org → Stripe Checkout for a NEW subscription.
          // Pricing source-of-truth: `defaultPriceSummary()` mirrors what the
          // server returns from `getStripePriceSummaries`. Used here purely
          // for the GTM payload — the actual Stripe transaction value lands
          // independently via webhooks.
          const priceSummary = defaultPriceSummary()[planKey]
          if (!priceSummary) {
            appToast.error(t('upgrade.checkoutFailed'))
            return
          }

          const result = await startTrackedCheckout({
            planKey,
            planName,
            planPriceCents: priceSummary.amountCents,
            planPriceCurrency: priceSummary.currency,
            // Limit dialogs unblock an action with the ordinary flexible plan.
            // The optional prepaid offer remains available on /plan, where the
            // customer can compare both choices and see the complete terms.
            cadence: 'monthly',
            userId,
            orgId,
            currentPlanName: usage?.planName,
          })

          if (!result.ok) {
            appToast.error(result.error)
            return
          }

          window.location.href = result.url
        })
        return
      }

      router.push('/plan')
      setIsOpen(false)
    },
    [router, orgId, userId, usage, canManageBilling, t]
  )

  const handleSecondary = useCallback(
    (cta: UpgradeLimitCta) => {
      if (cta.href) {
        if (cta.href.startsWith('/')) {
          router.push(cta.href)
        } else {
          window.open(cta.href, '_blank', 'noopener,noreferrer')
        }
        setIsOpen(false)
        return
      }
      setIsOpen(false)
    },
    [router]
  )

  const value = useMemo<UpgradeLimitContextValue>(
    () => ({
      showUpgrade,
      hideUpgrade,
      isUpgradePending,
    }),
    [showUpgrade, hideUpgrade, isUpgradePending]
  )

  // For an already-paid org the primary CTA reprices the subscription in place
  // (an immediate prorated charge) rather than opening Stripe Checkout — so we
  // disclose the charge here, matching the /plan grid's confirm dialog. Free
  // orgs go through Checkout (which discloses the monthly price), so no note
  // is needed there. The prepaid offer is never the primary recovery action;
  // eligible orgs see it as the rewritten "View all plans" secondary.

  const primaryNote = useMemo(() => {
    const planKey = config?.primaryCta.planKey
    if (!planKey) {
      return
    }

    const price = defaultPriceSummary()[planKey]

    // This action hands prepaid customers to /plan because Stripe cannot
    // prorate a trial. Explain the term instead of promising a charge.
    if (
      usage?.portalEligible &&
      isInPrepaidTerm({
        status: usage.status,
        isGrowthCommitment: usage.isGrowthCommitment,
        currentPeriodEnd: usage.currentPeriodEnd,
        nowMs: nowMsRef.current,
      })
    ) {
      // English from the shared refusal, shown through the errors catalog.
      return localizeErrorMessage(
        buildPrepaidTermChangeBlockedMessage(usage.currentPeriodEnd, locale)
      )
    }

    // No charge disclosure when the CTA won't charge here: a pending change or a
    // billing issue routes to /plan (resolve via "Keep" or Manage Billing).
    if (
      !usage?.portalEligible ||
      usage.hasScheduledPlanChange ||
      !isInAppRepriceableStatus(usage.status)
    ) {
      return
    }

    return price
      ? t('upgrade.proratedThen', {
          price: formatCurrency({
            amount: price.amount,
            currency: price.currency,
            locale,
          }),
        })
      : t('upgrade.prorated')
  }, [config, locale, t, usage])

  // When the primary action will HAND OFF to /plan instead of charging here
  // (prepaid term, scheduled change, payment issue), the button must not read
  // as an immediate upgrade — during a prepaid term the target card on /plan
  // is disabled, so "Upgrade to Pro" would promise an action that dead-ends.
  const displayConfig = useMemo(() => {
    if (!config?.primaryCta.planKey) {
      return config
    }
    const shouldDefer = shouldDeferPlanChangeToPlanPage({
      portalEligible: usage?.portalEligible ?? false,
      hasScheduledPlanChange: usage?.hasScheduledPlanChange ?? false,
      status: usage?.status,
      isGrowthCommitment: usage?.isGrowthCommitment,
      currentPeriodEnd: usage?.currentPeriodEnd,
      nowMs: nowMsRef.current,
    })
    if (!shouldDefer) {
      return config
    }
    return {
      ...config,
      primaryCta: { ...config.primaryCta, label: t('upgrade.viewPlanOptions') },
    }
  }, [config, t, usage])

  return (
    <UpgradeLimitContext.Provider value={value}>
      {children}
      {/* Mount only once a limit has fired (config stays set after close,
          so exit animations and re-opens keep working). */}
      {displayConfig !== null ? (
        <UpgradeLimitDialog
          open={isOpen}
          onOpenChange={handleOpenChange}
          config={displayConfig}
          onPrimary={handlePrimary}
          onSecondary={handleSecondary}
          isPrimaryPending={isUpgradePending}
          primaryNote={primaryNote}
        />
      ) : null}
    </UpgradeLimitContext.Provider>
  )
}

export function useUpgradeLimit(): UpgradeLimitContextValue {
  const ctx = useContext(UpgradeLimitContext)
  if (!ctx) {
    throw new Error(
      'useUpgradeLimit must be used within an UpgradeLimitDialogProvider'
    )
  }
  return ctx
}

export function useOptionalUpgradeLimit(): UpgradeLimitContextValue | null {
  return useContext(UpgradeLimitContext)
}
